import { useNode, useEditor, Element } from "@craftjs/core";
import React, { useRef, useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Sync,
  Person,
} from "@mui/icons-material";
import { useResponsive } from "../../hooks/useResponsive";
import { selectedOutlineSx } from "../../config/settingsStyles";
import {
  SliderField,
  CheckboxField,
  SettingsSection,
} from "../ui/form/SettingsField";
import { cmsApi } from "../../services/cmsApi";
import { useCMSConfig } from "../../context/CMSConfigContext";
import { DoctorCard } from "./DoctorCard";

// Component props
export interface DoctorsProps {
  gap?: number;
  showNavigation?: boolean;
  cardsPerViewDesktop?: number;
  cardsPerViewTablet?: number;
  cardsPerViewMobile?: number;
  maxWidth?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
}

// Empty state component
const EmptyState: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 6,
      px: 4,
      bgcolor: "#f9fafb",
      borderRadius: 2,
      border: "2px dashed #d1d5db",
      minHeight: 200,
      width: "100%",
    }}
  >
    <Person sx={{ fontSize: 48, color: "#9ca3af", mb: 2 }} />
    <Typography variant="h6" color="text.secondary" gutterBottom>
      No doctors yet
    </Typography>
    <Typography variant="body2" color="text.secondary" textAlign="center">
      Drag DoctorCard here or use Sync from API
    </Typography>
  </Box>
);

// Main Doctors container component
export const Doctors = ({
  gap = 24,
  showNavigation = true,
  cardsPerViewDesktop = 3,
  cardsPerViewTablet = 2,
  cardsPerViewMobile = 1,
  maxWidth = 1200,
  paddingX = 0,
  paddingTop = 0,
  paddingBottom = 0,
  children,
}: DoctorsProps & { children?: React.ReactNode }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isMobile, isTablet } = useResponsive(containerRef as React.RefObject<HTMLElement>);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);

  const {
    connectors: { connect, drag },
    selected,
    childNodes,
  } = useNode((state) => ({
    selected: state.events.selected,
    childNodes: state.data.nodes || [],
  }));

  const { actions, query } = useEditor();
  const childCount = childNodes.length;
  const prevChildCountRef = useRef(childCount);

  // Auto-apply style from first card to newly added cards
  useEffect(() => {
    const prevCount = prevChildCountRef.current;
    prevChildCountRef.current = childCount;

    // Only run if a new card was added (not removed)
    if (childCount <= prevCount || childCount < 2) return;

    // Get style from first card
    try {
      const firstChildId = childNodes[0];
      const firstChild = query.node(firstChildId).get();
      const firstProps = firstChild.data?.props || {};

      // Only if first card has custom style (not default)
      const hasCustomStyle = firstProps.backgroundColor && firstProps.backgroundColor !== "#ffffff";
      if (!hasCustomStyle) return;

      // Apply style to the last added card (newest one)
      const newChildId = childNodes[childCount - 1];
      const newChild = query.node(newChildId).get();
      const newProps = newChild.data?.props || {};

      // Only apply if new card has default style
      const isDefaultStyle = !newProps.backgroundColor || newProps.backgroundColor === "#ffffff";
      if (!isDefaultStyle) return;

      // Copy style from first card
      actions.setProp(newChildId, (props: any) => {
        props.backgroundColor = firstProps.backgroundColor;
        props.textColor = firstProps.textColor;
        props.borderRadius = firstProps.borderRadius;
        props.showShadow = firstProps.showShadow;
        props.imageSize = firstProps.imageSize;
        props.layout = firstProps.layout;
      });
    } catch (e) {
      // Ignore errors
    }
  }, [childCount, childNodes, query, actions]);

  // Calculate cards per view based on screen size
  const getCardsPerView = () => {
    if (isMobile) return cardsPerViewMobile;
    if (isTablet) return cardsPerViewTablet;
    return cardsPerViewDesktop;
  };

  const cardsPerView = getCardsPerView();

  // Calculate card width (with max limit)
  const [containerWidth, setContainerWidth] = useState(0);
  const MAX_CARD_WIDTH = 180;
  const calculatedWidth = containerWidth > 0
    ? Math.floor((containerWidth - (cardsPerView - 1) * gap - (showNavigation ? 80 : 0)) / cardsPerView)
    : 200;
  const cardWidth = Math.min(calculatedWidth, MAX_CARD_WIDTH);

  // Update container width with ResizeObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      setContainerWidth(container.clientWidth);
      if (scrollRef.current) {
        const maxScrollValue = Math.max(0, scrollRef.current.scrollWidth - scrollRef.current.clientWidth);
        setMaxScroll(maxScrollValue);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [childCount, isMobile, isTablet]);

  // Handle scroll (scroll by cardsPerView amount)
  const handleScroll = useCallback((direction: "left" | "right") => {
    if (!scrollRef.current) return;

    const scrollAmount = (cardWidth + gap) * cardsPerView;
    const newPosition =
      direction === "left"
        ? Math.max(0, scrollPosition - scrollAmount)
        : Math.min(maxScroll, scrollPosition + scrollAmount);

    scrollRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
    setScrollPosition(newPosition);
  }, [scrollPosition, maxScroll, cardWidth, gap, cardsPerView]);

  // Track scroll position
  const handleScrollUpdate = useCallback(() => {
    if (scrollRef.current) {
      setScrollPosition(scrollRef.current.scrollLeft);
      setMaxScroll(Math.max(0, scrollRef.current.scrollWidth - scrollRef.current.clientWidth));
    }
  }, []);

  // Check if cards actually overflow the container
  const totalCardsWidth = childCount * cardWidth + (childCount - 1) * gap;
  const needsScroll = totalCardsWidth > containerWidth;
  const showArrows = showNavigation && needsScroll;
  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollPosition < maxScroll - 1;

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        containerRef.current = ref;
        if (ref) connect(drag(ref));
      }}
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: `${maxWidth}px`,
        mx: "auto",
        px: `${paddingX}px`,
        pt: `${paddingTop}px`,
        pb: `${paddingBottom}px`,
        ...(selected && selectedOutlineSx),
      }}
    >
      <Box sx={{ position: "relative" }}>
        {/* Left Arrow */}
        {showArrows && (
          <IconButton
            onClick={() => handleScroll("left")}
            disabled={!canScrollLeft}
            sx={{
              position: "absolute",
              left: 4,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              bgcolor: "white",
              boxShadow: 2,
              "&:hover": { bgcolor: "grey.100" },
              "&:disabled": { opacity: 0.3 },
            }}
          >
            <ChevronLeft />
          </IconButton>
        )}

        {/* Carousel Container - This is the canvas for DoctorCard children */}
        <Box
          ref={scrollRef}
          onScroll={handleScrollUpdate}
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: `${gap}px`,
            overflowX: needsScroll ? "auto" : "visible",
            scrollBehavior: "smooth",
            px: 2,
            py: 2,
            minHeight: 180,
            justifyContent: needsScroll ? "flex-start" : "center",
            // Hide scrollbar but keep functionality
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            // Style for children (only DoctorCards, not EmptyState)
            "& > .doctor-card": {
              flex: `0 0 ${cardWidth}px`,
              maxWidth: `${cardWidth}px`,
            },
          }}
        >
          {childCount === 0 ? (
            <EmptyState />
          ) : (
            children
          )}
        </Box>

        {/* Right Arrow */}
        {showArrows && (
          <IconButton
            onClick={() => handleScroll("right")}
            disabled={!canScrollRight}
            sx={{
              position: "absolute",
              right: 4,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              bgcolor: "white",
              boxShadow: 2,
              "&:hover": { bgcolor: "grey.100" },
              "&:disabled": { opacity: 0.3 },
            }}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

// Settings component
export const DoctorsSettings = () => {
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const {
    actions: { setProp },
    gap,
    showNavigation,
    cardsPerViewDesktop,
    cardsPerViewTablet,
    cardsPerViewMobile,
    maxWidth,
    paddingX,
    paddingTop,
    paddingBottom,
    id,
  } = useNode((node) => ({
    gap: node.data.props.gap,
    showNavigation: node.data.props.showNavigation,
    cardsPerViewDesktop: node.data.props.cardsPerViewDesktop,
    cardsPerViewTablet: node.data.props.cardsPerViewTablet,
    cardsPerViewMobile: node.data.props.cardsPerViewMobile,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingTop: node.data.props.paddingTop,
    paddingBottom: node.data.props.paddingBottom,
    id: node.id,
  }));

  const { actions, query } = useEditor();
  const { apiToken, apiBaseUrl, divisionId } = useCMSConfig();

  const handleSyncDoctors = async () => {
    if (!cmsApi.isConfigured()) {
      setSyncError("API not configured");
      return;
    }

    setSyncing(true);
    setSyncError(null);

    try {
      const baseUrl = apiBaseUrl ?? "";
      const headers = {
        Authorization: `Token ${apiToken}`,
        division: String(divisionId),
      };

      // Fetch both ProviderSearch and DoctorList in parallel
      const [providerResponse, doctorResponse] = await Promise.all([
        fetch(`${baseUrl}/apis/QUE/scheduling/ProviderSearch/?division=${divisionId}`, { headers }),
        fetch(`${baseUrl}/apis/core/doctor/`, { headers }),
      ]);

      if (!providerResponse.ok) {
        throw new Error("Failed to fetch providers");
      }
      if (!doctorResponse.ok) {
        throw new Error("Failed to fetch doctors");
      }

      const providers = await providerResponse.json();
      const doctorData = await doctorResponse.json();
      const doctorList = doctorData.items || doctorData || [];

      // Map providers with doctor details
      const apiDoctors = (providers || []).map((provider: any) => {
        // Find doctor that has this provider_id in their provider array
        const foundDoctor = doctorList.find((doc: any) =>
          (doc.provider || []).includes(provider.provider_id)
        );

        return {
          providerId: provider.provider_id,
          name: foundDoctor?.full_name || `${provider.pre_name || ""}${provider.first_name} ${provider.last_name}`.trim(),
          specialty: (provider.specialty_name_list || []).join(", ") || foundDoctor?.specialty || "",
          imageUrl: foundDoctor?.image || "",
          gender: foundDoctor?.gender || null,
          certificateNo: foundDoctor?.certificate_no || "",
        };
      }).slice(0, 20); // Limit to 20

      // Get existing children
      const currentNode = query.node(id).get();
      const existingChildren = (currentNode.data?.nodes || []).slice(); // Clone array

      // Default style
      const defaultStyle = {
        backgroundColor: "#ffffff",
        textColor: "#1f2937",
        borderRadius: 12,
        showShadow: true,
        imageSize: "medium" as const,
        layout: "vertical" as const,
      };

      // Build map of existing cards by providerId + get style from first card
      const existingByProviderId = new Map<number, string>(); // providerId -> nodeId
      let cardStyle = { ...defaultStyle };

      for (let i = 0; i < existingChildren.length; i++) {
        const childId = existingChildren[i];
        try {
          const childNode = query.node(childId).get();
          const props = childNode.data?.props || {};

          // Get style from first card
          if (i === 0) {
            cardStyle = {
              backgroundColor: props.backgroundColor || defaultStyle.backgroundColor,
              textColor: props.textColor || defaultStyle.textColor,
              borderRadius: props.borderRadius ?? defaultStyle.borderRadius,
              showShadow: props.showShadow ?? defaultStyle.showShadow,
              imageSize: props.imageSize || defaultStyle.imageSize,
              layout: props.layout || defaultStyle.layout,
            };
          }

          // Map by providerId (only if has providerId)
          if (props.providerId) {
            existingByProviderId.set(props.providerId, childId);
          }
        } catch (e) {
          // Ignore
        }
      }

      // Process each doctor from API
      const processedProviderIds = new Set<number>();

      for (const doc of apiDoctors) {
        processedProviderIds.add(doc.providerId);
        const existingNodeId = existingByProviderId.get(doc.providerId);

        if (existingNodeId) {
          // Update existing card - only API fields
          actions.setProp(existingNodeId, (props: any) => {
            props.name = doc.name;
            props.specialty = doc.specialty;
            props.gender = doc.gender;
            props.certificateNo = doc.certificateNo;
            // Only update imageUrl if currently empty
            if (!props.imageUrl && doc.imageUrl) {
              props.imageUrl = doc.imageUrl;
            }
          });
        } else {
          // Add new card with inherited style
          const nodeTree = query.parseReactElement(
            <Element
              is={DoctorCard}
              providerId={doc.providerId}
              name={doc.name}
              specialty={doc.specialty}
              imageUrl={doc.imageUrl}
              gender={doc.gender}
              certificateNo={doc.certificateNo}
              backgroundColor={cardStyle.backgroundColor}
              textColor={cardStyle.textColor}
              borderRadius={cardStyle.borderRadius}
              showShadow={cardStyle.showShadow}
              imageSize={cardStyle.imageSize}
              layout={cardStyle.layout}
            />
          ).toNodeTree();

          actions.addNodeTree(nodeTree, id);
        }
      }

      // Note: Cards without providerId (manually added) are kept

    } catch (error) {
      setSyncError(error instanceof Error ? error.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <SettingsSection>
      {/* Sync button */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<Sync />}
        onClick={handleSyncDoctors}
        disabled={syncing}
        fullWidth
        sx={{ mb: 2 }}
      >
        {syncing ? "Syncing..." : "Sync Providers"}
      </Button>

      {syncError && (
        <Typography color="error" variant="caption" sx={{ display: "block", mb: 2 }}>
          {syncError}
        </Typography>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Display Settings
      </Typography>

      <CheckboxField
        label="Show Navigation Arrows"
        checked={showNavigation}
        onChange={(v) => setProp((props: DoctorsProps) => (props.showNavigation = v))}
      />

      <SliderField
        label="Gap"
        value={gap}
        onChange={(v) => setProp((props: DoctorsProps) => (props.gap = v))}
        min={8}
        max={48}
        unit="px"
      />

      <SliderField
        label="Cards (Desktop)"
        value={cardsPerViewDesktop}
        onChange={(v) => setProp((props: DoctorsProps) => (props.cardsPerViewDesktop = v))}
        min={1}
        max={6}
      />

      <SliderField
        label="Cards (Tablet)"
        value={cardsPerViewTablet}
        onChange={(v) => setProp((props: DoctorsProps) => (props.cardsPerViewTablet = v))}
        min={1}
        max={4}
      />

      <SliderField
        label="Cards (Mobile)"
        value={cardsPerViewMobile}
        onChange={(v) => setProp((props: DoctorsProps) => (props.cardsPerViewMobile = v))}
        min={1}
        max={2}
      />

      <SliderField
        label="Max Width"
        value={maxWidth}
        onChange={(v) => setProp((props: DoctorsProps) => (props.maxWidth = v))}
        min={0}
        max={1600}
        unit="px"
      />

      <SliderField
        label="Padding X"
        value={paddingX}
        onChange={(v) => setProp((props: DoctorsProps) => (props.paddingX = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Top"
        value={paddingTop}
        onChange={(v) => setProp((props: DoctorsProps) => (props.paddingTop = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Bottom"
        value={paddingBottom}
        onChange={(v) => setProp((props: DoctorsProps) => (props.paddingBottom = v))}
        min={0}
        max={120}
        unit="px"
      />
    </SettingsSection>
  );
};

// CraftJS configuration
Doctors.craft = {
  displayName: "Doctors",
  isCanvas: true,
  props: {
    gap: 24,
    showNavigation: true,
    cardsPerViewDesktop: 3,
    cardsPerViewTablet: 2,
    cardsPerViewMobile: 1,
    maxWidth: 1200,
    paddingX: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  rules: {
    canMoveIn: (incomingNodes: any[]) => {
      return incomingNodes.every((node) => node.data.type === DoctorCard);
    },
  },
  related: {
    settings: DoctorsSettings,
  },
};
